/**
 * What the reviewer reads before editing: the AI-extracted fields with the quote behind each, files read only in
 * part, industries the catalog does not have and partners it will add, and the gaps (missing values, placeholders, claims whose quote was not found).
 */

import type { ProposalOut } from '@/api/types/extractions';

import { CutNotice, GapsCard, TagNotices } from '../files/ProposalNotices';
import { fileNames, SourceNote, type FileNames } from '../files/SourceNote';
import type { ExtractedField, FromFilesDraft } from './fromFilesDraft';

const FIELD_LABELS: Record<ExtractedField, string> = {
  name: 'Name',
  tagline: 'Tagline',
  summary: 'Summary',
  maturity: 'Maturity',
  industries: 'Industries',
  partners: 'Partners',
  countries: 'Countries',
};

function ExtractedList({ draft, names }: { draft: FromFilesDraft; names: FileNames }) {
  const entries = (Object.keys(FIELD_LABELS) as ExtractedField[]).flatMap((key) => {
    const source = draft.extracted[key];
    return source ? [{ key, source }] : [];
  });
  if (entries.length === 0) return <p className="hint">The AI found none of the asset&apos;s fields in the files.</p>;
  return (
    <ul className="extraction-extracted" aria-label="Fields extracted by AI">
      {entries.map(({ key, source }) => (
        <li key={key}>
          <b>{FIELD_LABELS[key]}</b> <SourceNote source={source} names={names} what={FIELD_LABELS[key]} />
        </li>
      ))}
    </ul>
  );
}

export function ReviewNotices({ proposal, draft }: { proposal: ProposalOut; draft: FromFilesDraft }) {
  const names = fileNames(proposal.files);
  return (
    <div className="stack-md">
      <p className="alert alert-info" role="status">
        A draft proposed by AI from your files. Check each value against its quote; nothing is saved until you choose Create
        draft.
      </p>
      <CutNotice files={proposal.files} />
      <TagNotices
        industries={proposal.industry_suggestions}
        partners={proposal.unknown_partners}
        source="The files"
        partnersNote="They are in the draft and will be added as new partners when you create it; remove any you do not want."
      />
      <section className="card" aria-labelledby="extraction-fields-title">
        <div className="card-body stack-sm">
          <h2 id="extraction-fields-title">Extracted by AI</h2>
          <ExtractedList draft={draft} names={names} />
        </div>
      </section>
      <GapsCard gaps={proposal.gaps} id="extraction-gaps-title" />
    </div>
  );
}
